import { motion } from "framer-motion";
import { Card } from "antd";
import { CheckCircle, Star, ArrowRight } from "lucide-react";
import {
  fadeIn,
  staggerContainer,
  itemFadeIn,
  scaleIn,
} from "Features/LandingPage/Common";
import { useAnimateOnScroll } from "Hooks/useAnimateOnScroll";
import { StyledButton } from "Components/StyledButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import Footer from "Features/LandingPage/Footer/Footer";

export default function Scholarship({
  showCardsAndProcess = true,
  showFinalFooter = false,
}) {
  const [scholarshipRef, scholarshipControls] = useAnimateOnScroll(0.1);
  const navigate = useNavigate();
  return (
    <motion.section
      // @ts-ignore
      ref={scholarshipRef}
      initial="hidden"
      // @ts-ignore
      animate={scholarshipControls}
      variants={fadeIn}
      className="pt-8 md:pt-16 bg-gradient-to-br from-green-50 via-white to-green-100 relative overflow-hidden w-full"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div variants={fadeIn} className="text-center mb-4 md:mb-8">
          <div className="mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Earn $1,000 OneGrad
            <br />
            <span className="text-[#80E280]">Achievement Scholarship</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
            Exclusive scholarship opportunity for OneGrad Achiever and Mentored
            plan subscribers. Invest in your success and get rewarded for your
            commitment to excellence.
          </p>

          {/* Key Benefits */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600 mb-8">
            <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 text-base">
              <div className="w-2 h-2 bg-[#80E280] rounded-full mr-3 animate-pulse"></div>
              $1,000 Cash Award
            </div>
            <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 text-base">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></div>
              100% Plan Refund with OG Finance
            </div>
            <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 text-base">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 animate-pulse"></div>
              Merit-Based Selection
            </div>
          </div>
        </motion.div>

        {showCardsAndProcess && (
          <StyledButton
            onClick={() => {
              navigate(ROUTES.COLLEGE_FINDER);
            }}
            className="mb-4 md:mb-8 w-full md:w-1/3"
            variant="secondary"
          >
            Apply Now
          </StyledButton>
        )}

        {/* Eligibility & Benefits Grid */}
        {showCardsAndProcess && (
          <motion.div
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-12 mb-20"
          >
            {/* Eligibility Requirements */}
            <motion.div variants={itemFadeIn}>
              <Card className="h-full bg-white border-2 border-gray-200 hover:border-[#80E280] transition-all duration-300 hover:shadow-xl">
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-[#80E280] bg-opacity-10 rounded-xl flex items-center justify-center mr-4">
                      <CheckCircle className="w-7 h-7 text-[#80E280]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-2xl text-gray-900 mb-1 text-left">
                        Eligibility Requirements
                      </h3>
                      <div className="w-16 h-1 bg-[#80E280] rounded-full"></div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-[#80E280]">
                      <h4 className="font-semibold text-gray-900 mb-2 text-base text-left">
                        📋 Plan Requirements
                      </h4>
                      <p className="text-gray-700 text-base text-left">
                        Must be an active subscriber of OneGrad{" "}
                        <span className="font-semibold text-[#80E280]">
                          Achiever Plan
                        </span>{" "}
                        or
                        <span className="font-semibold text-[#80E280]">
                          {" "}
                          Mentored Plan
                        </span>
                      </p>
                    </div>

                    <ul className="space-y-4">
                      {[
                        "Active OneGrad Achiever or Mentored plan subscription",
                        "Completed profile with academic transcripts",
                        "Demonstrated commitment to study abroad goals",
                        "Participation in OneGrad community activities",
                        "Academic excellence (minimum 3.0 GPA equivalent)",
                      ].map((requirement, i) => (
                        <li
                          key={i}
                          className="flex items-start text-base text-gray-700"
                        >
                          <div className="w-5 h-5 bg-[#80E280] bg-opacity-10 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <CheckCircle className="w-3 h-3 text-[#80E280]" />
                          </div>
                          {requirement}
                        </li>
                      ))}
                    </ul>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">
                        💡 Pro Tip
                      </h4>
                      <p className="text-blue-700 text-sm">
                        Students using OG Finance get their Achiever/Mentored
                        plan fees
                        <span className="font-semibold">
                          {" "}
                          100% refunded
                        </span>{" "}
                        upon loan approval!
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Scholarship Benefits */}
            <motion.div variants={itemFadeIn}>
              <Card className="h-full bg-gradient-to-br from-[#80E280] to-green-400 text-white border-0 shadow-xl">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                    <Star className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-2xl text-white mb-1 text-left">
                      Scholarship Benefits
                    </h3>
                    <div className="w-16 h-1 bg-white bg-opacity-40 rounded-full"></div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold mb-2">$1,000</div>
                    <div className="text-lg opacity-90 font-bold">
                      Cash Scholarship Award
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      {
                        icon: "💰",
                        title: "Direct Cash Award",
                        description:
                          "$1,000 deposited directly to your account",
                      },
                      {
                        icon: "🎓",
                        title: "Academic Recognition",
                        description: "Official OneGrad Achievement Certificate",
                      },
                      {
                        icon: "🌟",
                        title: "Community Recognition",
                        description: "Featured in OneGrad success stories",
                      },
                      {
                        icon: "💳",
                        title: "Plan Refund Eligible",
                        description:
                          "Get 100% plan refund with OG Finance approval",
                      },
                    ].map((benefit, i) => (
                      <div key={i} className="flex items-start">
                        <div className="text-2xl mr-3 mt-1">{benefit.icon}</div>
                        <div>
                          <h4 className="font-semibold text-white mb-1 text-base text-left font-bold">
                            {benefit.title}
                          </h4>
                          <p className="text-white text-opacity-90 text-base text-left font-medium">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2 text-base font-bold">
                      🚀 Double Benefit
                    </h4>
                    <p className="text-white text-opacity-90 text-base font-medium">
                      Combine with OG Finance for maximum savings: $1,000
                      scholarship + 100% plan refund!
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}

        {/* Application Process */}
        {showCardsAndProcess && (
          <motion.div variants={fadeIn} className="mb-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Simple Application Process
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
                Four easy steps to apply for the OneGrad Achievement Scholarship
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "01",
                  title: "Subscribe to Plan",
                  description:
                    "Choose OneGrad Achiever or Mentored plan and complete your profile",
                  icon: "📝",
                  color: "bg-blue-50 text-blue-600",
                },
                {
                  step: "02",
                  title: "Meet Requirements",
                  description:
                    "Fulfill all eligibility criteria and maintain active participation",
                  icon: "✅",
                  color: "bg-green-50 text-green-600",
                },
                {
                  step: "03",
                  title: "Submit Application",
                  description:
                    "Complete the scholarship application with required documents",
                  icon: "📤",
                  color: "bg-purple-50 text-purple-600",
                },
                {
                  step: "04",
                  title: "Get Selected",
                  description:
                    "Merit-based selection process with results announced periodically",
                  icon: "🏆",
                  color: "bg-orange-50 text-orange-600",
                },
              ].map((step, index) => (
                <motion.div
                  key={index}
                  variants={itemFadeIn}
                  className="text-center relative"
                >
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#80E280] to-transparent transform -translate-x-1/2"></div>
                  )}
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-[#80E280] rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg">
                      {step.step}
                    </div>
                    <div className="text-3xl mb-3">{step.icon}</div>
                    <h4 className="font-bold text-gray-900 mb-2 text-base">
                      {step.title}
                    </h4>
                    <p className="text-base text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA Section */}
        {showCardsAndProcess && (
          <motion.div
            variants={scaleIn}
            className="text-center bg-gradient-to-br from-[#80E280] to-green-400 p-4 md:p-8 rounded-lg"
          >
            <p className="text-3xl text-white font-bold">
              Ready to Apply for the Scholarship?
            </p>
            <p className="text-base text-white font-semibold">
              Join OneGrad Achiever or Mentored plan and become eligible for the
              exclusive scholarship program.
            </p>
            <StyledButton
              onClick={() => {
                navigate(ROUTES.PRICING);
              }}
              variant="secondary"
              className="w-[90%] md:w-1/3 text-center text-base md:text-lg"
            >
              Subscribe to Achiever Plan
              <ArrowRight className="w-4 h-4 ml-2" />
            </StyledButton>
            <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-4">
              <div className="flex-1  bg-white/40 rounded-lg p-2 md:p-4">
                <p className="text-white font-bold text-center text-lg mb-1 mt-2">
                  Double Savings
                </p>
                <p className="text-white text-base mt-2">
                  $1000 Scholarship + 100% plan refund with OG Finance
                </p>
              </div>
              <div className="flex-1  bg-white/40 rounded-lg p-2 md:p-4">
                <p className="text-white font-bold text-center text-lg mb-1 mt-2">
                  Merit Based
                </p>
                <p className="text-white text-base mt-2">
                  Selection based on academic excellence and commitment
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {showCardsAndProcess && (
        <div className="w-full bg-gradient-to-r from-green-100 to-blue-100 px-4 sm:px-6 lg:px-8 mt-10 py-6 md:py-6">
          <div className="mx-auto max-w-4xl text-center py-10">
            <h2 className="text-3xl md:text-4xl font-bold">
              Let's Make Funding Your Education Simple
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-4 ">
              <div className="mt-1 md:mt-8 w-full flex-1">
                <StyledButton
                  onClick={() => {
                    navigate(
                      ROUTES.COLLEGE_FINDER_NEXT.replace(
                        ":next",
                        ROUTES.FINANCE,
                      ),
                    );
                  }}
                  className="w-full"
                >
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </StyledButton>
              </div>
              <div className="mt-1 md:mt-8 w-full flex-1">
                <StyledButton
                  onClick={() => {
                    navigate(
                      ROUTES.COLLEGE_FINDER_NEXT.replace(
                        ":next",
                        ROUTES.FINANCE,
                      ),
                    );
                  }}
                  variant="secondary"
                  className="w-full"
                >
                  Schedule a Call
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </StyledButton>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-base text-black font-semibold">
                No Hidden Fees, No Catch. Just the best options.
              </p>
            </div>
          </div>
        </div>
      )}
      {showFinalFooter && <Footer />}
    </motion.section>
  );
}
